import { onSnapshot } from 'firebase/firestore'
import Swal from 'sweetalert2'

// Watches appState/forceReload for { hash, force } and decides when to prompt
// the user to reload to a new build.
//
// SW lifecycle (configured in nuxt.config.ts):
//   - registerType: 'prompt' — vite-pwa does NOT auto-reload the page.
//   - workbox.skipWaiting: true — new SW becomes the registration's active
//     worker on install. The OLD SW keeps controlling the open tab until it
//     navigates/closes (no clientsClaim → no controllerchange → no surprise
//     reload during work). The next manual F5 is served by the new SW.
//
// This plugin:
//   1. Periodically pokes the SW to check for a new sw.js (browsers otherwise
//      cap update checks at 24h on long-lived tabs).
//   2. Listens to appState/forceReload. When `hash` flips vs the boot baseline
//      it pokes the SW again and arms `pendingFirestoreChange`.
//   3. When a new SW is ready AND the Firestore hash has flipped AND the user
//      is on a safe page (/v2/pos with table/dashboard tab), shows a modal:
//        - force=false → "Reload now" / "Later". Later keeps working; the
//          next manual F5 already gets the new build.
//        - force=true  → "Reload now (10)" countdown, auto-reloads.

export default defineNuxtPlugin(() => {
  const bootstrapStore = useBootstrapStore()
  const tabs = useTabs()
  const firestore = useFirestore()
  const router = useRouter()
  const { newVersionReady, pokeUpdate, applyUpdate } = usePwaUpdate()

  const isOnPosRoute = (path: string | undefined) =>
    !!path && (path === '/v2/pos' || path.startsWith('/v2/pos'))

  const isAllowedTab = (tab: string | null | undefined) =>
    tab === 'table' || tab === 'dashboard'

  let unsubscribe: (() => void) | undefined
  let pollTimer: ReturnType<typeof setInterval> | undefined
  let bootPokeTimer: ReturnType<typeof setTimeout> | undefined
  let baselineHash: string | null | undefined // undefined = not yet read
  const pendingFirestoreChange = ref(false)
  const forceMode = ref(false)
  const alerted = ref(false)

  const POLL_MS = 12 * 60 * 60 * 1000 // 12 hours
  // const POLL_MS = 60 * 60 * 1000   // 60 min

  watch(
    () => bootstrapStore.loaded,
    (isLoaded) => {
      if (isLoaded) start()
      else stop()
    },
    { immediate: true }
  )

  watchEffect(() => {
    if (
      pendingFirestoreChange.value &&
      newVersionReady.value &&
      !alerted.value &&
      isOnPosRoute(router.currentRoute.value.path) &&
      isAllowedTab(tabs.tabSelected.value)
    ) {
      showReloadModal(forceMode.value)
    }
  })

  function start() {
    if (unsubscribe) return

    bootPokeTimer = setTimeout(() => pokeUpdate(), 30_000)
    pollTimer = setInterval(() => pokeUpdate(), POLL_MS)

    unsubscribe = onSnapshot(
      firestore.getForceReloadRef(),
      (snap) => {
        const data = snap.data()
        const remoteHash = (data?.hash ?? null) as string | null
        const remoteForce = data?.force === true

        // The FIRST snapshot of any kind (cached or server) sets the baseline.
        // Previously we skipped fromCache snapshots before baseline was set,
        // which produced a "need to change hash twice" bug on slow connections:
        // if a cached snapshot arrived first, it was dropped; then a server
        // snapshot carrying the user's first change became the baseline
        // instead of triggering the modal.
        if (baselineHash === undefined) {
          baselineHash = remoteHash
          return
        }

        // After baseline is set, ignore cached snapshots so we don't bounce on
        // offline cache flips. Only act on confirmed server changes.
        if (snap.metadata.fromCache) return

        if (remoteHash !== baselineHash) {
          pendingFirestoreChange.value = true
          forceMode.value = remoteForce
          pokeUpdate()
        }
      },
      (err) => {
        console.error('[force-reload] snapshot error', err)
      }
    )
  }

  function stop() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = undefined
    }
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = undefined
    }
    if (bootPokeTimer) {
      clearTimeout(bootPokeTimer)
      bootPokeTimer = undefined
    }
  }

  function showReloadModal(force: boolean) {
    alerted.value = true

    if (force) {
      let secondsLeft = 10
      Swal.fire({
        icon: 'info',
        title: 'New version available',
        text: 'Updating now to apply the latest changes.',
        confirmButtonText: `Reload now (${secondsLeft})`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCancelButton: false,
        timer: 10_000,
        timerProgressBar: true,
        didOpen: () => {
          const btn = Swal.getConfirmButton()
          const tick = setInterval(() => {
            secondsLeft -= 1
            if (btn) btn.textContent = `Reload now (${secondsLeft})`
            if (secondsLeft <= 0) clearInterval(tick)
          }, 1000)
        },
      }).then(() => applyUpdate())
      return
    }

    Swal.fire({
      icon: 'info',
      title: 'New version available',
      text: 'Reload now to get the latest version, or continue and update later.',
      showCancelButton: true,
      confirmButtonText: 'Reload now',
      cancelButtonText: 'Later',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((res) => {
      if (res.isConfirmed) applyUpdate()
    })
  }
})
